import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import {
  renderGrid,
  getByPath,
  formatFileFieldDisplay,
} from "../common/FormRenderer";
import {
  PPC_PICKLIST_LABELS,
  buildCreateFieldsForPpc,
  PPC_MEDIA_UPLOAD_FIELDS,
} from "./projectPageComponentColumnsConfig";
import {
  getPresignedUploadUrl,
  uploadFileToPresignedUrl,
  deleteS3Object,
} from "../../services/backend/s3Api";
import { validateProjectPageComponentEditForm } from "../../config/projectPageComponentEdit.validation";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_PAGE_COMPONENT } from "../../config/sobjectNames";
import ImageLocationFieldInline from "./ImageLocationFieldInline";

export default function CreateProjectPageComponentModal({
  visible,
  onClose,
  onSuccess,
  initialValues,
}) {
  const { picklistMap } = usePicklistMap(
    Object.values(PPC_PICKLIST_LABELS),
    SOBJECT_PROJECT_PAGE_COMPONENT,
  );

  const [form, setForm] = useState({
    projectPage: "",
    recordType: "",
    componentOrder: "",
    description: "",
    headline1: "",
    headline2: "",
    headline3: "",
    textArea1: "",
    textArea2: "",
    textArea3: "",
    buttonType: "",
    buttonType2: "",
    buttonType3: "",
    buttonName1: "",
    buttonName2: "",
    buttonName3: "",
    buttonUrl1: "",
    buttonUrl2: "",
    buttonUrl3: "",
    imageLocation1: "",
    imageLocation2: "",
    imageLocation3: "",
    fileUrl1: "",
    fileUrl2: "",
    fileUrl3: "",
  });
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [pendingImageFiles, setPendingImageFiles] = useState({
    imageLocation1: null,
    imageLocation2: null,
    imageLocation3: null,
  });
  const objectUrlRefs = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    if (initialValues !== undefined) {
      const { projectPageId, projectPageName } = initialValues;
      if (projectPageId) {
        initialValues = {
          projectPageId: projectPageId,
          projectPage: projectPageName,
          ...initialValues,
        };
      }
    }
    if (visible && initialValues && typeof initialValues === "object") {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialValues]);

  const handleImageDelete = (path) => {
    const value = getByPath(form, path);
    if (value && typeof value === "string" && !value.startsWith("http")) {
      deleteS3Object(value.trim()).catch(() => {});
    }
    handleChange(path, "");
    setPendingImageFiles((s) => ({ ...s, [path]: null }));
  };

  const handleImageRemovePath = (path) => {
    handleChange(path, "");
    setPendingImageFiles((s) => ({ ...s, [path]: null }));
  };

  const handleSave = async () => {
    const validationPayload = {
      info: {
        projectPageId: form.projectPage,
        componentOrder: form.componentOrder,
      },
      content: {
        buttonType1: form.buttonType,
        buttonType2: form.buttonType2,
      },
    };

    const errors = validateProjectPageComponentEditForm(validationPayload);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setSaving(true);
    try {
      const pathToApiName = {
        imageLocation1: "Image_Location_1__c",
        imageLocation2: "Image_Location_2__c",
        imageLocation3: "Image_Location_3__c",
      };

      const hasPendingUploads = Object.values(pendingImageFiles).some(
        (f) => f && f instanceof File,
      );
      let formWithUploads = { ...form };
      const projectPageId = form.projectPage || form.projectPageId;
      const uploadId = Date.now();

      for (const [path, file] of Object.entries(pendingImageFiles)) {
        if (!file || !(file instanceof File)) continue;
        const apiName = pathToApiName[path];
        if (!apiName) continue;
        const key = `${projectPageId || "new"}/${uploadId}/${apiName}/${file.name}`;
        const { url } = await getPresignedUploadUrl({
          key,
          contentType: file.type || undefined,
          expiresIn: 900,
        });
        await uploadFileToPresignedUrl(url, file);
        formWithUploads[path] = key;
      }

      const { createIndividualProjectPageComponent } =
        await import("../../services/projectPageComponentService");

      const result =
        await createIndividualProjectPageComponent(formWithUploads);

      if (result) {
        message.success("Project Page Component created successfully!");
        setForm({
          projectPage: "",
          recordType: "",
          componentOrder: "",
          description: "",
          headline1: "",
          headline2: "",
          headline3: "",
          textArea1: "",
          textArea2: "",
          textArea3: "",
          buttonType: "",
          buttonType2: "",
          buttonType3: "",
          buttonName1: "",
          buttonName2: "",
          buttonName3: "",
          buttonUrl1: "",
          buttonUrl2: "",
          buttonUrl3: "",
          imageLocation1: "",
          imageLocation2: "",
          imageLocation3: "",
          fileUrl1: "",
          fileUrl2: "",
          fileUrl3: "",
        });
        setPendingImageFiles({
          imageLocation1: null,
          imageLocation2: null,
          imageLocation3: null,
        });
        onSuccess();
        navigate(`/ppc/${result[0].Id}`);
      } else {
        message.error(
          result?.error?.message || "Failed to create Project Page Component",
        );
      }
    } catch (error) {
      console.error("Error creating project page component:", error);
      message.error(
        error.message ||
          "An error occurred while creating the Project Page Component",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      projectPage: "",
      recordType: "",
      componentOrder: "",
      description: "",
      headline1: "",
      headline2: "",
      headline3: "",
      textArea1: "",
      textArea2: "",
      textArea3: "",
      buttonType: "",
      buttonType2: "",
      buttonType3: "",
      buttonName1: "",
      buttonName2: "",
      buttonName3: "",
      buttonUrl1: "",
      buttonUrl2: "",
      buttonUrl3: "",
      imageLocation1: "",
      imageLocation2: "",
      imageLocation3: "",
      fileUrl1: "",
      fileUrl2: "",
      fileUrl3: "",
    });
    setPendingImageFiles({
      imageLocation1: null,
      imageLocation2: null,
      imageLocation3: null,
    });
    setValidationErrors({});
    onClose();
  };

  const handleChange = (path, value, displayName) => {
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
    setForm((prev) => {
      const update = { ...prev, [path]: value };
      if (displayName !== undefined) {
        update[`${path}DisplayName`] = displayName;
      }
      return update;
    });
  };

  const setPendingImageFile = (path, file) => {
    if (objectUrlRefs.current[path]) {
      URL.revokeObjectURL(objectUrlRefs.current[path]);
      objectUrlRefs.current[path] = null;
    }
    if (file) {
      objectUrlRefs.current[path] = URL.createObjectURL(file);
      setForm((prev) => ({ ...prev, [path]: file.name }));
    }
    setPendingImageFiles((s) => ({ ...s, [path]: file }));
  };

  const allContentFields = resolvePicklistFields(
    buildCreateFieldsForPpc(form),
    picklistMap,
  );

  const imagePaths = new Set([
    "imageLocation1",
    "imageLocation2",
    "imageLocation3",
  ]);

  const gridFields = allContentFields.filter((f) => !imagePaths.has(f.path));

  const allFieldPaths = new Set();
  allContentFields.forEach((f) => {
    if (f.path) allFieldPaths.add(f.path);
    if (f.fields)
      f.fields.forEach((sf) => {
        if (sf.path) allFieldPaths.add(sf.path);
      });
  });

  const visibleMediaFields = PPC_MEDIA_UPLOAD_FIELDS.map((mf) => ({
    ...mf,
    path: mf.path.replace("content.", ""),
  })).filter((mf) => allFieldPaths.has(mf.path));
  // Get validation error messages directly from centralized validation
  const errorMessages = Object.values(validationErrors);

  return (
    <Modal
      title="Create Project Page Component"
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={saving}
      width={800}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: 0 }}
    >
      {/* Validation Error Banner */}
      {errorMessages.length > 0 && (
        <div className="sticky top-0 z-10 px-6 py-3 bg-red-50 border-b border-red-200">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-red-800 mb-1.5">
                {errorMessages.length} error
                {errorMessages.length !== 1 ? "s" : ""} found:
              </h4>
              <div className="max-h-24 overflow-y-auto pr-2">
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {errorMessages.map((error, index) => (
                    <li key={index} className="leading-relaxed">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="space-y-4 p-6">
        {renderGrid(gridFields, {
          formData: form,
          onChange: handleChange,
          showValidation: true,
        })}
        {visibleMediaFields.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {visibleMediaFields.map((f) => (
              <ImageLocationFieldInline
                key={f.path}
                label={f.label}
                path={f.path}
                value={getByPath(form, f.path)}
                onChange={handleChange}
                onPendingFile={setPendingImageFile}
                pendingFile={pendingImageFiles[f.path]}
                onDelete={handleImageDelete}
                onRemovePath={handleImageRemovePath}
                isCreate={true}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
